import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Icon } from '../../components/common/Icon';
import { AgentAvatar } from '../../components/agent/AgentAvatar';
import {
  type LeaderboardCallPath,
  type LeaderboardCategory,
  type LeaderboardSort,
  CATEGORY_META,
  LEADERBOARD_CATEGORIES,
  sortCallPaths,
  successRate,
} from '../../types/leaderboardCallPath';
import { fetchMockCallPathLeaderboard } from '../../mocks/leaderboardCallPath';

/**
 * Call-path leaderboard.
 *
 * Ranks `(agent, ability)` invocation pairs by real engineering metrics
 * (`runCount`, `successCount`, `avgElapsedMs`, `lastInvokedAt`) instead
 * of social/agent metrics. The smallest unit of execution in the
 * EasyNet ontology is the call path, so the leaderboard ranks call
 * paths.
 *
 * The board is organised as a three-level drill-down so users land in
 * something they can scan, not a flat 100-row table:
 *
 *     L1  Categories      → coarse partitions (chat / code / vision …)
 *     L2  Ability names   → distinct ability names within a category,
 *                           collapsed across the agents that implement
 *                           them
 *     L3  Call-path rows  → the original ranking, filtered to a single
 *                           ability name so every row competes on the
 *                           same axis
 *
 * The same `LeaderboardCallPath[]` powers all three levels — L1 and L2
 * are derived in `useMemo` from the flat list, so a future
 * `/abilities/leaderboard` API doesn't need any new endpoints.
 */

/** Pills shown at the top of the leaderboard. Four of them are real
 * sort dimensions over the flat call-path list; the fifth (`category`)
 * is a *view switch* that pivots the page into a categories →
 * abilities → rankings drill-down. We model both as the same control
 * because that's how the user thinks of it: "show me the board,
 * organized by …". */
type LeaderboardPill = LeaderboardSort | 'category';

const PILL_OPTIONS: { key: LeaderboardPill; labelKey: string; icon: string }[] = [
  { key: 'calls',    labelKey: 'discover.sortCalls',    icon: 'bolt' },
  { key: 'success',  labelKey: 'discover.sortSuccess',  icon: 'verified' },
  { key: 'latency',  labelKey: 'discover.sortLatency',  icon: 'speed' },
  { key: 'recency',  labelKey: 'discover.sortRecency',  icon: 'schedule' },
  { key: 'category', labelKey: 'discover.sortCategory', icon: 'category' },
];

const RANK_STYLES = ['text-amber-500', 'text-gray-400', 'text-orange-600'];
const RANK_ICONS = ['trophy', 'military_tech', 'military_tech'];

// ─── Formatters ────────────────────────────────────────────────────────────

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000)    return `${(n / 1000).toFixed(1)}k`;
  if (n >= 1000)      return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
}

function formatLatency(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(rate >= 0.99 ? 2 : 1)}%`;
}

function formatRelative(iso: string, t: TFunction): string {
  const elapsed = Date.now() - new Date(iso).getTime();
  const m = Math.floor(elapsed / 60_000);
  if (m < 1)    return t('discover.justNow', 'now');
  if (m < 60)   return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}d`;
  return `${Math.floor(d / 30)}mo`;
}

/** Map a success rate (0..1) to a tailwind color class. Used for the
 * mini progress bar and the percentage label so the row reads at a
 * glance: emerald = excellent, amber = warn, red = problem. */
function successColor(rate: number): { bar: string; text: string } {
  if (rate >= 0.98) return { bar: 'bg-emerald-500',  text: 'text-emerald-600 dark:text-emerald-400' };
  if (rate >= 0.95) return { bar: 'bg-green-500',    text: 'text-green-600 dark:text-green-400' };
  if (rate >= 0.85) return { bar: 'bg-amber-500',    text: 'text-amber-600 dark:text-amber-400' };
  return                  { bar: 'bg-red-500',      text: 'text-red-600 dark:text-red-400' };
}

/** Drill-down position when the user picks the `category` pill.
 * Modeled as a discriminated union so render code never has to guess
 * whether a category or ability has been selected. Ignored entirely
 * when any of the four flat sort pills are active. */
type LeaderboardView =
  | { level: 'categories' }
  | { level: 'abilities'; category: LeaderboardCategory }
  | { level: 'rankings'; category: LeaderboardCategory; abilityName: string };

// ─── Component ─────────────────────────────────────────────────────────────

export function LeaderboardTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pill, setPill] = useState<LeaderboardPill>('calls');
  const [items, setItems] = useState<LeaderboardCallPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [view, setView] = useState<LeaderboardView>({ level: 'categories' });

  // The `category` pill is a view switch, not a sort, so it doesn't
  // map to a sort dimension. Inside the drill-down's L3 (rankings)
  // we still need *some* sort, and `calls` is the most useful default
  // because that's what users are most often comparing on.
  const isCategoryView = pill === 'category';
  const sortBy: LeaderboardSort = isCategoryView ? 'calls' : pill;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const result = await fetchMockCallPathLeaderboard();
        if (!cancelled) setItems(result);
      } catch {
        if (!cancelled) {
          setItems([]);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ─── Derived data ─────────────────────────────────────────────────
  //
  // Flat mode renders `sorted` directly (the original behavior).
  // byCategory mode walks a three-level drill-down: L3 (rankings)
  // wants the original sorted, filtered list; L2 (abilities) collapses
  // items by `abilityName`; L1 (categories) further collapses those
  // by `category`. All derivations are pure functions of `items` so
  // the component re-renders cheaply.

  /** Items visible in flat mode — original behavior, the entire list
   * sorted by the active dimension. */
  const sorted = useMemo(() => sortCallPaths(items, sortBy), [items, sortBy]);

  /** Items visible at L3 — filtered to the selected ability name and
   * sorted by the active sort dimension. */
  const rankingItems = useMemo(() => {
    if (view.level !== 'rankings') return [] as LeaderboardCallPath[];
    const filtered = items.filter(
      (e) => e.category === view.category && e.abilityName === view.abilityName,
    );
    return sortCallPaths(filtered, sortBy);
  }, [items, view, sortBy]);

  /** Rollups visible at L2 — one entry per distinct ability name in
   * the selected category. We pick the highest-volume implementation
   * as the "headline" so the user can see who currently leads each
   * ability at a glance, and we sum the engineering metrics across
   * all implementations. */
  const abilityRollups = useMemo(() => {
    if (view.level === 'categories') return [] as AbilityRollup[];
    const category =
      view.level === 'abilities' ? view.category : view.category;
    const inCategory = items.filter((e) => e.category === category);
    return rollupByAbility(inCategory);
  }, [items, view]);

  /** Rollups visible at L1 — one entry per category, derived from the
   * full ability list. Empty categories are dropped so the grid only
   * shows partitions that actually have content. */
  const categoryRollups = useMemo(() => rollupByCategory(items), [items]);

  // Aggregate stats for the top bar — scoped to the visible content:
  //   flat pill           → entire board
  //   category pill, L1   → entire board
  //   category pill, L2   → the selected category
  //   category pill, L3   → the selected ability
  // Same shape regardless so the StatBar JSX doesn't branch.
  const aggregate = useMemo(() => {
    const scope: LeaderboardCallPath[] =
      !isCategoryView || view.level === 'categories'
        ? items
        : view.level === 'abilities'
        ? items.filter((e) => e.category === view.category)
        : rankingItems;
    if (scope.length === 0) {
      return { totalCalls: 0, abilityCount: 0, avgSuccess: 0 };
    }
    const totalCalls   = scope.reduce((acc, e) => acc + e.runCount, 0);
    const totalSuccess = scope.reduce((acc, e) => acc + e.successCount, 0);
    // For flat-pill / L1 / L2 the meaningful "ability count" is
    // distinct ability names. At L3 every row is the same ability,
    // so the count becomes the number of agents implementing it.
    const abilityCount =
      isCategoryView && view.level === 'rankings'
        ? scope.length
        : new Set(scope.map((e) => e.abilityName)).size;
    return {
      totalCalls,
      abilityCount,
      avgSuccess: totalSuccess / Math.max(totalCalls, 1),
    };
  }, [items, view, isCategoryView, rankingItems]);

  const handleCopy = async (id: string, callPath: string) => {
    try {
      await navigator.clipboard.writeText(callPath);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // clipboard permission denied — silent
    }
  };

  // Body content depends on the active pill (and, when the category
  // pill is active, the drill-down level). Each branch owns its own
  // empty / loading state so spacing stays consistent.
  let body: JSX.Element;
  if (loading) {
    body = (
      <div className="text-center py-16">
        <p className="text-gray-400 text-sm">{t('common.loading')}</p>
      </div>
    );
  } else if (!isCategoryView) {
    body = (
      <>
        <div data-tour="leaderboard">
          {sorted.map((entry, index) => (
            <CallPathRow
              key={entry.id}
              entry={entry}
              rank={index + 1}
              sortBy={sortBy}
              copied={copiedId === entry.id}
              onCopy={() => handleCopy(entry.id, `${entry.agentName}.${entry.abilityName}`)}
              onOpen={() => navigate(`/agent/${entry.agentId}`)}
              t={t}
            />
          ))}
        </div>
        {sorted.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-sm">
              {loadFailed ? 'Load failed' : t('discover.noAgents')}
            </p>
          </div>
        )}
      </>
    );
  } else if (view.level === 'categories') {
    body = (
      <CategoryGrid
        rollups={categoryRollups}
        loadFailed={loadFailed}
        onSelect={(category) => setView({ level: 'abilities', category })}
        t={t}
      />
    );
  } else if (view.level === 'abilities') {
    body = (
      <AbilityList
        rollups={abilityRollups}
        category={view.category}
        onSelect={(abilityName) =>
          setView({ level: 'rankings', category: view.category, abilityName })
        }
        t={t}
      />
    );
  } else {
    body = (
      <div>
        {rankingItems.map((entry, index) => (
          <CallPathRow
            key={entry.id}
            entry={entry}
            rank={index + 1}
            sortBy={sortBy}
            copied={copiedId === entry.id}
            onCopy={() => handleCopy(entry.id, `${entry.agentName}.${entry.abilityName}`)}
            onOpen={() => navigate(`/agent/${entry.agentId}`)}
            t={t}
          />
        ))}
        {rankingItems.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-sm">{t('discover.noAgents')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Compact summary strip — three numbers on one line, divided by
          hairlines. No card container, no padding inflation: the
          information density matches a WeChat list header. */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
        <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-white/[0.06]">
          <StatCell
            label={t('discover.statTotalCalls', 'Total invocations')}
            value={formatCount(aggregate.totalCalls)}
          />
          <StatCell
            label={t('discover.statAbilityCount', 'Active abilities')}
            value={aggregate.abilityCount.toString()}
            center
          />
          <StatCell
            label={t('discover.statAvgSuccess', 'Avg success')}
            value={formatPercent(aggregate.avgSuccess)}
            valueClass={successColor(aggregate.avgSuccess).text}
            right
          />
        </div>
      </div>

      {/* Pill bar — four sort dimensions plus a "category" pill that
          flips the page into a drill-down view. Same row, same shape,
          because users think of these as the same control: "show me
          the board, organized by …". */}
      <div className="flex items-center gap-1.5 px-4 py-2 overflow-x-auto hide-scrollbar border-b border-gray-100 dark:border-gray-800">
        {PILL_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => {
              setPill(opt.key);
              // Re-entering category view always lands on the
              // categories grid, not wherever the drill-down was left.
              if (opt.key === 'category') setView({ level: 'categories' });
            }}
            className={`
              flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors
              ${pill === opt.key
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }
            `}
          >
            <Icon name={opt.icon} size={13} />
            {t(opt.labelKey)}
          </button>
        ))}
      </div>

      {/* Breadcrumb — only renders below L1 of the category drill-down
          so the categories grid stays as clean as possible. Tapping a
          crumb pops back up the stack rather than walking history. */}
      {isCategoryView && view.level !== 'categories' && (
        <Breadcrumb view={view} setView={setView} t={t} />
      )}

      {body}
    </div>
  );
}

// ─── Shared row primitive ──────────────────────────────────────────────────

interface RowProps {
  /** 40×40 leading element. Avatar, status indicator, or icon tile. */
  avatar: ReactNode;
  /** Title row. Plain string for simple cases, ReactNode when the
   * row mixes badges/buttons inline (the leaderboard call-path row). */
  title: ReactNode;
  /** One-line subtitle. Always rendered with a `text-gray-500` color
   * to match WeChat's secondary text. Pass `undefined` to hide. */
  subtitle?: ReactNode;
  /** Right-aligned metadata: timestamp, count, badge. The right
   * column is right-aligned and stacks vertically. */
  meta?: ReactNode;
  /** Optional drill-in affordance — pass `true` to render a small
   * gray chevron after the meta column. WeChat uses chevrons for
   * settings/drill-down rows but never on chat list rows, so we
   * make it explicit at the call site. */
  chevron?: boolean;
  onClick?: () => void;
}

/**
 * Canonical list row used across the Discover screen — and the only
 * row primitive any new Alive screen should reach for. Shape and
 * proportions are tuned to match WeChat's iOS conversation list:
 *
 *   - 40×40 leading slot, 12px gutter, then content
 *   - 16-pixel title (`text-base`), `font-medium`, gray-900
 *   - 13-pixel subtitle (`text-xs`), gray-500
 *   - hairline divider that **starts after the avatar gutter**, the
 *     iOS / WeChat signature detail. We achieve this by putting the
 *     `border-b` on the inner content wrapper instead of the row
 *     outer, so the avatar column is never crossed by a line.
 *   - hover and active states cover the entire row, including the
 *     avatar column, so the affordance is unmistakable
 */
function Row({ avatar, title, subtitle, meta, chevron, onClick }: RowProps) {
  return (
    <div
      onClick={onClick}
      className="
        group flex items-stretch px-4 cursor-pointer
        hover:bg-gray-50 dark:hover:bg-white/[0.03]
        active:bg-gray-100 dark:active:bg-white/[0.06]
        transition-colors
      "
    >
      <div className="flex-shrink-0 flex items-center py-3">
        {avatar}
      </div>
      <div className="
        ml-3 flex-1 min-w-0 flex items-center gap-3
        py-3 border-b border-gray-100 dark:border-white/[0.06]
        group-last:border-b-0
      ">
        <div className="flex-1 min-w-0">
          {typeof title === 'string' ? (
            <div className="text-base font-medium text-gray-900 dark:text-gray-100 truncate">
              {title}
            </div>
          ) : (
            title
          )}
          {subtitle != null && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
              {subtitle}
            </div>
          )}
        </div>
        {meta != null && (
          <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
            {meta}
          </div>
        )}
        {chevron && (
          <Icon
            name="chevron_right"
            size={18}
            className="flex-shrink-0 text-gray-300 dark:text-gray-600 -mr-1"
          />
        )}
      </div>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

interface StatCellProps {
  label: string;
  value: string;
  valueClass?: string;
  center?: boolean;
  right?: boolean;
}

function StatCell({ label, value, valueClass, center, right }: StatCellProps) {
  return (
    <div
      className={`
        flex flex-col gap-0.5 px-3
        ${center ? 'items-center text-center' : right ? 'items-end text-right' : 'items-start'}
      `}
    >
      <span className={`text-base font-semibold tabular-nums leading-tight ${valueClass ?? 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
        {label}
      </span>
    </div>
  );
}

interface CallPathRowProps {
  entry: LeaderboardCallPath;
  rank: number;
  sortBy: LeaderboardSort;
  copied: boolean;
  onCopy: () => void;
  onOpen: () => void;
  t: TFunction;
}

function CallPathRow({ entry, rank, sortBy, copied, onCopy, onOpen, t }: CallPathRowProps) {
  const isTopThree = rank <= 3;
  const rate = successRate(entry);
  const successPalette = successColor(rate);

  // The primary metric is the big number on the right; the secondary
  // metric is one tiny line beneath it. We never show more than two
  // numbers in the right column — WeChat conversation rows show
  // timestamp + unread badge and we mirror that two-line rhythm.
  const primary = primaryMetric(entry, sortBy, t);
  const secondary = compactSecondary(entry, sortBy, rate, successPalette.text, t);

  return (
    <Row
      onClick={onOpen}
      avatar={
        <div className="relative">
          <AgentAvatar
            avatar={entry.agentAvatar}
            status={entry.agentStatus}
            size="md"
          />
          {/* Top-three rank badge — sits over the bottom-right of
              the avatar, same place WeChat puts the unread bubble.
              Beyond top-three the digit moves to the title row so
              every avatar slot stays the same width. */}
          {isTopThree && (
            <span className={`
              absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full
              flex items-center justify-center
              bg-white dark:bg-[#171719] ring-1 ring-white dark:ring-[#171719]
            `}>
              <Icon
                name={RANK_ICONS[rank - 1]}
                size={14}
                className={RANK_STYLES[rank - 1]}
                filled
              />
            </span>
          )}
        </div>
      }
      title={
        <div className="flex items-center gap-1 min-w-0">
          {!isTopThree && (
            <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 tabular-nums mr-0.5">
              {rank}
            </span>
          )}
          <span className="text-base font-medium text-gray-900 dark:text-gray-100 truncate">
            {entry.agentName}
          </span>
          <span className="text-base text-gray-300 dark:text-gray-600 select-none">.</span>
          <span className="text-base font-mono text-primary truncate">
            {entry.abilityName}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
            className="
              ml-0.5 p-1 -my-1 rounded
              text-gray-300 dark:text-gray-600
              hover:text-primary
              md:opacity-0 md:group-hover:opacity-100
              transition-opacity
            "
            aria-label={t('discover.copyCallPath', 'Copy call path')}
          >
            <Icon
              name={copied ? 'check' : 'content_copy'}
              size={13}
              className={copied ? 'text-emerald-500' : ''}
            />
          </button>
        </div>
      }
      subtitle={entry.abilityDescription}
      meta={
        <>
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
              {primary.value}
            </span>
            {primary.unit && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{primary.unit}</span>
            )}
          </div>
          <span className={`text-[11px] tabular-nums ${secondary.className}`}>
            {secondary.value}
          </span>
        </>
      }
    />
  );
}

// ─── Metric pickers ────────────────────────────────────────────────────────

interface MetricCell {
  value: string;
  unit?: string;
}

/** The single secondary line under the primary metric in the right
 * column. We pick *one* qualifier that gives the most context for the
 * current sort:
 *   - sorting by calls or recency  → success rate (the universal
 *     "is this thing actually working?" answer)
 *   - sorting by success           → invocation volume (because a
 *     100% rate over 5 calls is meaningless)
 *   - sorting by latency           → invocation volume (same reason)
 * Mirrors WeChat list rows that show one stat + one qualifier and
 * never a four-line stack.
 */
interface CompactSecondary {
  value: string;
  className: string;
}

function primaryMetric(
  entry: LeaderboardCallPath,
  sortBy: LeaderboardSort,
  t: TFunction,
): MetricCell {
  switch (sortBy) {
    case 'calls':
      return { value: formatCount(entry.runCount), unit: t('discover.unitCalls', 'calls') };
    case 'success':
      return { value: formatPercent(successRate(entry)) };
    case 'latency':
      return { value: formatLatency(entry.avgElapsedMs) };
    case 'recency':
      return { value: formatRelative(entry.lastInvokedAt, t), unit: t('discover.unitAgo', 'ago') };
  }
}

function compactSecondary(
  entry: LeaderboardCallPath,
  sortBy: LeaderboardSort,
  rate: number,
  successTextClass: string,
  t: TFunction,
): CompactSecondary {
  switch (sortBy) {
    case 'calls':
    case 'recency':
      // Volume is already the headline (or implied by recency); the
      // useful qualifier is "did it actually work?".
      return { value: formatPercent(rate), className: successTextClass };
    case 'success':
    case 'latency':
      // Headline is a quality/speed number — anchor it with the call
      // volume so the user knows the sample size.
      return {
        value: `${formatCount(entry.runCount)} ${t('discover.unitCallsShort', 'calls')}`,
        className: 'text-gray-400 dark:text-gray-500',
      };
  }
}

// ─── Drill-down rollups ────────────────────────────────────────────────────

/** Aggregate of all call paths sharing the same `abilityName`. This is
 * what the L2 view renders — one card per distinct ability — and what
 * the L1 view sums over to compute its category cards.
 *
 * `topImpl` is the highest-volume implementation. It's surfaced on the
 * L2 card so the user can see who currently leads each ability without
 * having to drill in. */
interface AbilityRollup {
  abilityName: string;
  category: LeaderboardCategory;
  /** Distinct agents implementing this ability. */
  implCount: number;
  totalCalls: number;
  totalSuccess: number;
  /** Average elapsed time across implementations, weighted by call
   * volume so a tiny test agent doesn't skew the number. */
  weightedAvgElapsedMs: number;
  /** Most recent invocation across all implementations. ISO. */
  latestInvokedAt: string;
  topImpl: LeaderboardCallPath;
  /** Display description / tags from the top implementation — used as
   * the headline copy on the L2 card. */
  description: string;
  tags: string[];
}

interface CategoryRollup {
  category: LeaderboardCategory;
  abilityCount: number;
  totalCalls: number;
  totalSuccess: number;
  /** Highest-volume ability in the category — surfaced on the L1 card
   * as a "trending" hint. */
  topAbility: string;
}

/** Group `LeaderboardCallPath` rows by `abilityName`. Returns rollups
 * sorted by total invocation volume so the L2 list always leads with
 * the most-used abilities. */
function rollupByAbility(rows: LeaderboardCallPath[]): AbilityRollup[] {
  const groups = new Map<string, LeaderboardCallPath[]>();
  for (const row of rows) {
    const list = groups.get(row.abilityName);
    if (list) list.push(row);
    else groups.set(row.abilityName, [row]);
  }
  const rollups: AbilityRollup[] = [];
  for (const [abilityName, group] of groups) {
    const totalCalls   = group.reduce((acc, e) => acc + e.runCount, 0);
    const totalSuccess = group.reduce((acc, e) => acc + e.successCount, 0);
    const weightedSum  = group.reduce((acc, e) => acc + e.avgElapsedMs * e.runCount, 0);
    const topImpl = group.reduce((best, e) => (e.runCount > best.runCount ? e : best), group[0]);
    const latest  = group.reduce(
      (acc, e) => (e.lastInvokedAt > acc ? e.lastInvokedAt : acc),
      group[0].lastInvokedAt,
    );
    rollups.push({
      abilityName,
      category: topImpl.category,
      implCount: group.length,
      totalCalls,
      totalSuccess,
      weightedAvgElapsedMs: totalCalls === 0 ? 0 : weightedSum / totalCalls,
      latestInvokedAt: latest,
      topImpl,
      description: topImpl.abilityDescription,
      tags: topImpl.abilityTags,
    });
  }
  rollups.sort((a, b) => b.totalCalls - a.totalCalls);
  return rollups;
}

/** Group ability rollups by category. Categories are emitted in the
 * canonical order from `LEADERBOARD_CATEGORIES` (not by call volume)
 * so the L1 grid is stable across sessions and refreshes. Empty
 * categories are dropped. */
function rollupByCategory(rows: LeaderboardCallPath[]): CategoryRollup[] {
  const byCat = new Map<LeaderboardCategory, LeaderboardCallPath[]>();
  for (const row of rows) {
    const list = byCat.get(row.category);
    if (list) list.push(row);
    else byCat.set(row.category, [row]);
  }
  const out: CategoryRollup[] = [];
  for (const category of LEADERBOARD_CATEGORIES) {
    const group = byCat.get(category);
    if (!group || group.length === 0) continue;
    const totalCalls   = group.reduce((acc, e) => acc + e.runCount, 0);
    const totalSuccess = group.reduce((acc, e) => acc + e.successCount, 0);
    const distinctAbilities = new Set(group.map((e) => e.abilityName));
    const top = group.reduce((best, e) => (e.runCount > best.runCount ? e : best), group[0]);
    out.push({
      category,
      abilityCount: distinctAbilities.size,
      totalCalls,
      totalSuccess,
      topAbility: top.abilityName,
    });
  }
  return out;
}

// ─── L1 — category grid ────────────────────────────────────────────────────

interface CategoryGridProps {
  rollups: CategoryRollup[];
  loadFailed: boolean;
  onSelect: (category: LeaderboardCategory) => void;
  t: TFunction;
}

function CategoryGrid({ rollups, loadFailed, onSelect, t }: CategoryGridProps) {
  if (rollups.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-400 text-sm">
          {loadFailed ? 'Load failed' : t('discover.noAgents')}
        </p>
      </div>
    );
  }
  // Vertical list of category entries — same `Row` primitive as the
  // L3 ranking, just with an icon tile instead of an avatar.
  return (
    <div>
      {rollups.map((r) => {
        const rate = r.totalSuccess / Math.max(r.totalCalls, 1);
        const successPalette = successColor(rate);
        const meta = CATEGORY_META[r.category];
        return (
          <Row
            key={r.category}
            onClick={() => onSelect(r.category)}
            avatar={
              <div className="
                w-10 h-10 rounded flex items-center justify-center
                bg-primary/10 text-primary
              ">
                <Icon name={meta.icon} size={22} />
              </div>
            }
            title={t(meta.labelKey)}
            subtitle={
              <span className="flex items-center gap-1.5">
                <span>{t('discover.categoryAbilityCount', { count: r.abilityCount })}</span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="tabular-nums">
                  {formatCount(r.totalCalls)} {t('discover.unitCallsShort', 'calls')}
                </span>
              </span>
            }
            meta={
              <span className={`text-sm font-medium tabular-nums ${successPalette.text}`}>
                {formatPercent(rate)}
              </span>
            }
            chevron
          />
        );
      })}
    </div>
  );
}

// ─── L2 — ability list within a category ──────────────────────────────────

interface AbilityListProps {
  rollups: AbilityRollup[];
  category: LeaderboardCategory;
  onSelect: (abilityName: string) => void;
  t: TFunction;
}

function AbilityList({ rollups, onSelect, t }: AbilityListProps) {
  if (rollups.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-400 text-sm">{t('discover.noAgents')}</p>
      </div>
    );
  }
  // Three drill-down levels, one row template (`Row`). The visual
  // language doesn't change — only the data does.
  return (
    <div>
      {rollups.map((r) => {
        const rate = r.totalSuccess / Math.max(r.totalCalls, 1);
        const successPalette = successColor(rate);
        return (
          <Row
            key={r.abilityName}
            onClick={() => onSelect(r.abilityName)}
            avatar={
              <div className="
                w-10 h-10 rounded flex items-center justify-center
                bg-primary/10 text-primary
              ">
                <Icon name={CATEGORY_META[r.category].icon} size={22} />
              </div>
            }
            title={
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-base font-mono font-medium text-primary truncate">
                  {r.abilityName}
                </span>
                <span className="flex-shrink-0 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                  {t('discover.abilityImplCount', { count: r.implCount })}
                </span>
              </div>
            }
            subtitle={
              <span className="flex items-center gap-1.5">
                <span className="truncate">
                  {t('discover.topAgent', 'Top')} · {r.topImpl.agentName}
                </span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="tabular-nums">{formatLatency(r.weightedAvgElapsedMs)}</span>
              </span>
            }
            meta={
              <>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                    {formatCount(r.totalCalls)}
                  </span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {t('discover.unitCallsShort', 'calls')}
                  </span>
                </div>
                <span className={`text-[11px] tabular-nums ${successPalette.text}`}>
                  {formatPercent(rate)}
                </span>
              </>
            }
            chevron
          />
        );
      })}
    </div>
  );
}

// ─── Breadcrumb ────────────────────────────────────────────────────────────

interface BreadcrumbProps {
  view: Exclude<LeaderboardView, { level: 'categories' }>;
  setView: (view: LeaderboardView) => void;
  t: TFunction;
}

/** Breadcrumb that doubles as the back affordance. Tapping any segment
 * pops the view stack to that level — we don't walk router history
 * because the drill-down state lives entirely in this component. */
function Breadcrumb({ view, setView, t }: BreadcrumbProps) {
  const meta = CATEGORY_META[view.category];
  // Tight WeChat-style nav bar — back arrow + parent label on the
  // left, current title centered, no decoration. Tap anywhere
  // outside the title to pop one level up.
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
      <button
        type="button"
        onClick={() =>
          setView(
            view.level === 'rankings'
              ? { level: 'abilities', category: view.category }
              : { level: 'categories' },
          )
        }
        className="flex-shrink-0 flex items-center gap-0.5 -ml-1 pr-1 py-0.5 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <Icon name="chevron_left" size={16} />
        <span>
          {view.level === 'rankings'
            ? t(meta.labelKey)
            : t('discover.backToCategories', 'Categories')}
        </span>
      </button>
      <div className="flex-1 min-w-0 text-center">
        {view.level === 'abilities' ? (
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t(meta.labelKey)}
          </span>
        ) : (
          <span className="text-sm font-mono font-semibold text-primary truncate inline-block max-w-full align-middle">
            {view.abilityName}
          </span>
        )}
      </div>
      {/* Spacer mirrors the back-button width so the centered title
          stays optically centered. */}
      <div className="flex-shrink-0 w-16" aria-hidden />
    </div>
  );
}
